import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ageValidator } from '@webapp/core/helpers/age.validator';
import { InputAddressComponent } from '@webapp/core/ui/components/input-address/input-address.component';
import { ButtonComponent, InputErrorComponent } from '@webapp/ui';
import { GROSS_INCOME_CONDITION, isUserType, IUser, USER_TYPES } from '../../../interfaces/users.interface';
import { CommonModule } from '@angular/common';
import { NgxMaskDirective } from 'ngx-mask';
import { CuitBlurNormalizeDirective, cuitValidator } from '@webapp/core';
import { ZonesService } from '@webapp/core/services/zones.service';
import { IZone } from '@webapp/core/interfaces/zones.interface';

@Component({
  selector: 'app-user-update-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputAddressComponent,
    ButtonComponent,
    InputErrorComponent,
    NgxMaskDirective,
    CuitBlurNormalizeDirective,
  ],
  templateUrl: './user-update-form.component.html',
  styleUrls: ['./user-update-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserUpdateFormComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly zonesService = inject(ZonesService);

  // Inputs
  user = input.required<IUser>();
  loading = input<boolean>(false);

  // Outputs
  userUpdated = output<IUser>();

  // Signals
  readonly USER_TYPES = USER_TYPES;
  readonly GROSS_INCOME_CONDITION = GROSS_INCOME_CONDITION;
  readonly submitted = signal(false);
  readonly form = signal<FormGroup | null>(null);
  readonly zones = signal<IZone[]>([]);
  readonly selectedZone = signal<string>('');
  readonly checkingPostalCode = signal(false);

  ngOnInit(): void {
    this.initializeForm();
  }

  private initializeForm(): void {
    const userData = this.user();

    const isPersonType = isUserType(userData.type, USER_TYPES.PERSON);
    const isCompanyType = isUserType(userData.type, USER_TYPES.COMPANY);
  

    const formGroup = this.formBuilder.group({
      firstName: [
        userData.firstName || '',
        isPersonType ? [Validators.required] : [],
      ],
      lastName: [
        userData.lastName || '',
        isPersonType ? [Validators.required] : [],
      ],
      companyName: [
        userData.companyName || '',
        isCompanyType ? [Validators.required] : [],
      ],
      identification: [
        userData.identification || '',
        isCompanyType ? [Validators.required, cuitValidator] : [Validators.required],
      ],
      email: [
        userData.email || '',
        [Validators.required, Validators.email],
      ],
      grossIncomeCondition: [
        userData.grossIncomeCondition || '',
        isCompanyType ? [Validators.required] : [],
      ],
      birthDate: [
        userData.birthDate || '',
        isPersonType ? [Validators.required, ageValidator] : [],
      ],
      address: [userData.address || '', [Validators.required]],
      postalCode: [userData.postalCode || '', [Validators.required]],
      zoneId: [userData.zoneId || ''],
    });

    this.form.set(formGroup);
  }

  protected get isPersonType(): boolean {
    return isUserType(this.user().type, USER_TYPES.PERSON);
  }

  protected get isCompanyType(): boolean {
    return isUserType(this.user().type, USER_TYPES.COMPANY);
  }

  protected get identificationLabel(): string {
    return this.isPersonType ? 'DNI' : 'CUIT';
  }

  protected get identificationMask(): string {
    return this.isPersonType ? '00.000.000' : '00-00000000-0';
  }

  protected onSubmit(): void {
    if (!this.form()) return;

    this.submitted.set(true);

    // Check if multiple zones are available and none is selected
    if (this.zones().length > 1 && !this.selectedZone()) {
      return;
    }

    const form = this.form();
    form?.markAllAsTouched();
    if (form?.valid) {
      const formValues = form.getRawValue();
      
      const updatedUser: IUser = {
        ...this.user(),
        ...formValues,
        // Ensure zoneId is properly included
        zoneId: formValues.zoneId || this.selectedZone() || this.user().zoneId,
      };

      // Emit the updated user data, let parent handle the API call
      this.userUpdated.emit(updatedUser);
    }
  }

  protected checkPostalCode(): void {
    const postalCode = this.form()?.get('postalCode')?.value;
    if (!postalCode) return;

    this.checkingPostalCode.set(true);
    this.zonesService.getZones(postalCode).subscribe({
      next: (response) => {
        this.checkingPostalCode.set(false);
        const zones = response;
        if (zones.length === 1) {
          this.form()?.get('zoneId')?.setValue(zones[0].id);
          this.form()?.get('zoneId')?.markAsDirty();
          this.selectedZone.set(zones[0].id);
          this.zones.set([]);
        } else {
          this.zones.set(zones);
          this.selectedZone.set('');
          this.form()?.get('zoneId')?.setValue('');
        }
      },
      error: () => {
        this.checkingPostalCode.set(false);
        this.zones.set([]);
        this.selectedZone.set('');
      },
    });
  }

  protected onZoneSelect(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const zoneId = target.value;
    this.selectedZone.set(zoneId);
    this.form()?.get('zoneId')?.setValue(zoneId);
    this.form()?.get('zoneId')?.markAsDirty();
  }
}